import CallModal from 'app/components/call/CallModal';
import BrowserSpecific from 'app/components/common/BrowserSpecific';
import {
  ChromeDialog,
  ChromeToolbar,
  FirefoxDialog,
  FirefoxPermission,
  FirefoxToolbar,
  SafariDialog,
  SafariMenu,
} from 'app/components/DeviceSetup/browserScreens';
import WithSkip from 'app/components/DeviceSetup/WithSkip';
import { T, tFunctionType } from 'app/components/localization';
import AlertBox, { AlertVariant } from 'app/components/presentation/alert/AlertBox';
import { ButtonA } from 'app/components/presentation/buttons/ButtonClassNameA';
import { GetHistory } from 'app/components/router/History';
import {
  DeviceState,
  DeviceType,
  GetDeviceManager,
  GetDevices,
  UserAction,
} from 'app/containers/rtc/DeviceManagerContext';
import { Browser, IPlatformParams, OperatingSystem, Platform } from 'app/utils/userAgent';
import React, { Fragment } from 'react';

interface IProps {
  type: DeviceType;
}

interface IPropsWithDeps extends IProps {
  userAgent: IPlatformParams;
  device: DeviceState;
  reload: () => void;
  dismiss: () => void;
  manualStep: () => void;
}

const messagesPerType = {
  [DeviceType.Audio]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.change-browser-settings.audio.title'),
    intro: t('rtc.device-setup.change-browser-settings.audio.message'),
  }),
  [DeviceType.Video]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.change-browser-settings.video.title'),
    intro: t('rtc.device-setup.change-browser-settings.video.message'),
  }),
};

const BrowserSettingsChangeHelp = ({
  type,
  dismiss, manualStep, reload,
  device,
  userAgent: { os, platform, browser },
}: IPropsWithDeps) => (
  <T>{t => {
    const { title, intro } = messagesPerType[type](t);

    const needsManualRetry = device.userAction === UserAction.ChangeBrowserSettingsAndRetry;
    const needsReload = device.userAction === UserAction.ChangeBrowserSettingsAndReload;

    const customHelp =
      os === OperatingSystem.iOS ? <IOSSafariHelp /> :
      platform === Platform.PC ? (
        browser === Browser.Chrome ? <PCChromeHelp type={type} /> :
        browser === Browser.Firefox ? <PCFirefoxHelp type={type} /> :
        browser === Browser.Safari ? <MacSafariHelp /> :
        null
      ) : null;

    return (
      <CallModal
        buttons={
          <WithSkip type={type}>
            {(needsReload || needsManualRetry ?
              (needsManualRetry
                ? <ButtonA children={t('rtc.try-again')} onClick={manualStep} />
                : <ButtonA children={t('rtc.device-setup.reload')} onClick={reload} />)
              : undefined)
            }
          </WithSkip>
        }
        onClose={dismiss}
        title={title}
      >
        {/* The alert is already being rendered inside a modal, so simply use it as a presentation. */}
        <AlertBox variant={AlertVariant.Danger}>
          {intro}
          {' '}
          {customHelp
            ? t('rtc.device-setup.change-browser-settings.follow-instructions')
            : t('rtc.device-setup.change-browser-settings.reload-only')}
        </AlertBox>

        {
          customHelp || (
            // it will say to reload in the red box above, this is
            // sumplentary text for OS-level permissions
            browser !== Browser.Safari && (
              os === OperatingSystem.Android ||
              os === OperatingSystem.macOS ||
              os === OperatingSystem.iOS
            ) && (
              <p>
                {t('rtc.device-setup.change-browser-settings.os-permission')}
              </p>
            )
          )
        }

      </CallModal>
    );
  }}</T>
);

const MacSafariHelp = () =>
  <T>{t =>
    <Fragment>
      <p>{t('rtc.device-setup.change-browser-settings.safari.open-menu')}</p>
      <SafariMenu />
      <p>{t('rtc.device-setup.change-browser-settings.safari.allow-access')}</p>
      <SafariDialog />
      <p>{t('rtc.device-setup.change-browser-settings.reload-after')}</p>
    </Fragment>
  }</T>;

const IOSSafariHelp = () =>
  <T>{(t) =>
    <p>{t('rtc.device-setup.change-browser-settings.ios.reload')}</p>
  }</T>;

const PCChromeHelp = ({ type }: { type: DeviceType }) =>
  <T>{t =>
    <Fragment>
      <p>{t('rtc.device-setup.change-browser-settings.chrome.open-dialog')}</p>
      <ChromeToolbar />
      <p>{t('rtc.device-setup.change-browser-settings.chrome.allow-access')}</p>
      <ChromeDialog deviceType={type} />
      <p>{t('rtc.device-setup.change-browser-settings.chrome.after-changes')}</p>
    </Fragment>
  }</T>;

const PCFirefoxHelp = ({ type }: { type: DeviceType }) =>
  <T>{t =>
    <Fragment>
      <p>{t('rtc.device-setup.change-browser-settings.firefox.open-dialog')}</p>
      <FirefoxToolbar />
      <p>{t('rtc.device-setup.change-browser-settings.firefox.unlock', {
        label: type === DeviceType.Audio
          ? t('rtc.device-setup.change-browser-settings.firefox.dialog.use-microphone')
          : t('rtc.device-setup.change-browser-settings.firefox.dialog.use-camera'),
      })}</p>
      <FirefoxDialog deviceType={type} />
      <p>{t('rtc.device-setup.change-browser-settings.firefox.try-again')}</p>
      <p>{t('rtc.device-setup.initial.firefox.check-box')}</p>
      <FirefoxPermission />
    </Fragment>
  }</T>;

const BrowserSettingsChangeHelpInjectDependencies = (props: IProps) =>
  <GetHistory>{(history: { reload: () => void }) =>
    <GetDeviceManager>{(manager) => manager &&
      <GetDevices>{(devices) => devices &&
        <BrowserSpecific>{(userAgent) =>
          <BrowserSettingsChangeHelp
            {...props}
            userAgent={userAgent}
            manualStep={manager.step}
            dismiss={manager.dismiss}
            reload={history.reload}
            device={props.type === DeviceType.Audio ? devices.audio : devices.video}
          />
        }</BrowserSpecific>
      }</GetDevices>
    }</GetDeviceManager>
  }</GetHistory>;

export default BrowserSettingsChangeHelpInjectDependencies;
